import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import { IssuePeekOverview } from '@/components/IssuePeekOverview';
import { CreateGanttItemModal } from '@/components/CreateGanttItemModal';
import { ChevronLeft, ChevronRight, Calendar, Plus } from 'lucide-react';
import clsx from 'clsx';

const DAY_WIDTH = 40;
const ROW_HEIGHT = 40;

export function GanttPage() {
  const { projectId } = useParams<{ workspaceId: string; projectId: string }>();
  const api = useApi();

  const [viewStart, setViewStart] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); // Start 1 week ago
    return date;
  });

  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: workItems, refetch: refetchWorkItems } = useQuery({
    queryKey: ['workitems', projectId],
    queryFn: () => api.getWorkItems(projectId!),
    enabled: !!projectId,
  });

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId!),
    enabled: !!projectId,
  });

  // Calculate view range (6 weeks)
  const viewDays = 42;
  const viewEnd = useMemo(() => {
    const end = new Date(viewStart);
    end.setDate(end.getDate() + viewDays);
    return end;
  }, [viewStart]);

  // Generate day headers
  const days = useMemo(() => {
    const result = [];
    const current = new Date(viewStart);
    for (let i = 0; i < viewDays; i++) {
      result.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return result;
  }, [viewStart]);

  // Group days by week
  const weeks = useMemo(() => {
    const result: { start: Date; days: Date[] }[] = [];
    let currentWeek: Date[] = [];
    let weekStart: Date | null = null;

    days.forEach((day, i) => {
      if (day.getDay() === 0 || i === 0) {
        if (currentWeek.length > 0) {
          result.push({ start: weekStart!, days: currentWeek });
        }
        currentWeek = [];
        weekStart = day;
      }
      currentWeek.push(day);
    });

    if (currentWeek.length > 0) {
      result.push({ start: weekStart!, days: currentWeek });
    }

    return result;
  }, [days]);

  // Items with dates for Gantt
  const ganttItems = useMemo(() => {
    if (!workItems) return [];

    return workItems
      .filter((item: any) => item.bh_startdate || item.bh_targetdate)
      .map((item: any) => {
        const start = item.bh_startdate ? new Date(item.bh_startdate) : new Date(item.bh_targetdate);
        const end = item.bh_targetdate ? new Date(item.bh_targetdate) : new Date(item.bh_startdate);

        // Ensure end is after start
        if (end < start) {
          return { ...item, startDate: end, endDate: start };
        }

        return { ...item, startDate: start, endDate: end };
      })
      .sort((a: any, b: any) => a.startDate.getTime() - b.startDate.getTime());
  }, [workItems]);

  // Items without dates
  const itemsWithoutDates = useMemo(() => {
    if (!workItems) return [];
    return workItems.filter((item: any) => !item.bh_startdate && !item.bh_targetdate);
  }, [workItems]);

  const getStateColor = (stateId: string) => {
    const state = states?.find((s: any) => s.bh_stateid === stateId);
    return state?.bh_color || '#6b7280';
  };

  const getBarPosition = (startDate: Date, endDate: Date) => {
    const viewStartTime = viewStart.getTime();
    const viewEndTime = viewEnd.getTime();

    // Clamp dates to view range
    const barStart = Math.max(startDate.getTime(), viewStartTime);
    const barEnd = Math.min(endDate.getTime(), viewEndTime);

    if (barEnd < viewStartTime || barStart > viewEndTime) {
      return null; // Not visible
    }

    const dayMs = 24 * 60 * 60 * 1000;
    const left = ((barStart - viewStartTime) / dayMs) * DAY_WIDTH;
    const width = Math.max(((barEnd - barStart) / dayMs + 1) * DAY_WIDTH, DAY_WIDTH);

    return { left, width };
  };

  const navigateWeeks = (direction: number) => {
    setViewStart((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + direction * 7);
      return newDate;
    });
  };

  const goToToday = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    setViewStart(date);
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isWeekend = (date: Date) => {
    return date.getDay() === 0 || date.getDay() === 6;
  };

  const formatMonth = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="h-full flex flex-col" style={{ minHeight: 0 }}>
      {/* Header - Fixed at top, never scrolls */}
      <div className="flex-none p-4 border-b border-gray-200 bg-white flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Gantt Chart</h1>
        <div className="flex items-center gap-4">
          {/* Navigation controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateWeeks(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              Today
            </button>
            <button
              onClick={() => navigateWeeks(1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Create Item Button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
          >
            <Plus className="w-4 h-4" />
            Create Item
          </button>
        </div>
      </div>

      {/* Gantt Chart - Only this area scrolls */}
      <div className="flex-1 overflow-auto" style={{ minHeight: 0 }}>
        <div className="min-w-max">
          {/* Timeline Header */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
            {/* Weeks row */}
            <div className="flex">
              <div className="w-64 flex-shrink-0 px-4 py-2 bg-gray-50 border-r border-gray-200">
                <span className="text-xs font-semibold text-gray-500 uppercase">Task</span>
              </div>
              <div className="flex">
                {weeks.map((week, i) => (
                  <div
                    key={i}
                    className="border-r border-gray-200 bg-gray-50 px-2 py-1"
                    style={{ width: week.days.length * DAY_WIDTH }}
                  >
                    <span className="text-xs font-medium text-gray-600">
                      {formatMonth(week.start)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Days row */}
            <div className="flex">
              <div className="w-64 flex-shrink-0 px-4 py-1 bg-gray-50 border-r border-gray-200">
                <span className="text-xs text-gray-400">
                  {ganttItems.length} items with dates
                </span>
              </div>
              <div className="flex">
                {days.map((day, i) => (
                  <div
                    key={i}
                    className={clsx(
                      'flex-shrink-0 text-center py-1 border-r border-gray-100 text-xs',
                      isToday(day) && 'bg-primary-50 font-semibold text-primary-700',
                      isWeekend(day) && !isToday(day) && 'bg-gray-50 text-gray-400'
                    )}
                    style={{ width: DAY_WIDTH }}
                  >
                    {day.getDate()}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Gantt Rows */}
          <div>
            {ganttItems.length === 0 ? (
              <div className="flex">
                <div className="w-64 flex-shrink-0" />
                <div className="flex-1 py-12 text-center text-gray-500">
                  No items with dates. Add start/target dates to see them here.
                </div>
              </div>
            ) : (
              ganttItems.map((item: any) => {
                const position = getBarPosition(item.startDate, item.endDate);

                return (
                  <div
                    key={item.bh_workitemid}
                    className="flex border-b border-gray-100 hover:bg-gray-50"
                    style={{ height: ROW_HEIGHT }}
                  >
                    {/* Task name */}
                    <div
                      onClick={() => setSelectedIssueId(item.bh_workitemid)}
                      className="w-64 flex-shrink-0 px-4 flex items-center border-r border-gray-200 cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: getStateColor(item._bh_state_value) }}
                        />
                        <span className="text-sm text-gray-900 truncate" title={item.bh_name}>
                          {item.bh_name}
                        </span>
                      </div>
                    </div>

                    {/* Timeline */}
                    <div className="relative flex-1">
                      {/* Weekend backgrounds */}
                      {days.map((day, i) => (
                        <div
                          key={i}
                          className={clsx(
                            'absolute top-0 bottom-0',
                            isWeekend(day) && 'bg-gray-50',
                            isToday(day) && 'bg-primary-50/50'
                          )}
                          style={{ left: i * DAY_WIDTH, width: DAY_WIDTH }}
                        />
                      ))}

                      {/* Today line */}
                      {days.map((day, i) =>
                        isToday(day) && (
                          <div
                            key={`today-${i}`}
                            className="absolute top-0 bottom-0 w-0.5 bg-primary-500 z-10"
                            style={{ left: i * DAY_WIDTH + DAY_WIDTH / 2 }}
                          />
                        )
                      )}

                      {/* Task bar */}
                      {position && (
                        <div
                          onClick={() => setSelectedIssueId(item.bh_workitemid)}
                          className="absolute top-2 rounded-md shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all flex items-center px-2 text-white text-xs font-medium overflow-hidden"
                          style={{
                            left: position.left,
                            width: position.width,
                            height: ROW_HEIGHT - 16,
                            backgroundColor: getStateColor(item._bh_state_value),
                          }}
                          title={`${item.bh_name}\n${item.startDate.toLocaleDateString()} - ${item.endDate.toLocaleDateString()}`}
                        >
                          <span className="truncate">{item.bh_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Items without dates */}
          {itemsWithoutDates.length > 0 && (
            <div className="border-t-2 border-gray-200 mt-4">
              <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase">
                Items without dates ({itemsWithoutDates.length})
              </div>
              {itemsWithoutDates.map((item: any) => (
                <div
                  key={item.bh_workitemid}
                  onClick={() => setSelectedIssueId(item.bh_workitemid)}
                  className="flex border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  style={{ height: ROW_HEIGHT }}
                >
                  <div className="w-64 flex-shrink-0 px-4 flex items-center">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: getStateColor(item._bh_state_value) }}
                      />
                      <span className="text-sm text-gray-500 truncate">
                        {item.bh_name}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 flex items-center px-4 text-xs text-gray-400">
                    No dates set
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Issue Peek Overview */}
      {selectedIssueId && (
        <IssuePeekOverview
          issueId={selectedIssueId}
          projectId={projectId!}
          onClose={() => setSelectedIssueId(null)}
        />
      )}

      {/* Create Item Modal */}
      {showCreateModal && projectId && (
        <CreateGanttItemModal
          projectId={projectId}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            refetchWorkItems();
          }}
          defaultStartDate={new Date()}
          defaultEndDate={(() => {
            const d = new Date();
            d.setDate(d.getDate() + 7);
            return d;
          })()}
        />
      )}
    </div>
  );
}
